import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SEOHead } from "@/components/SEOHead";
import AdminLogin from "./AdminLogin";
import { Search, TrendingUp, Users, Globe, Smartphone, Monitor, Tablet } from "lucide-react";
import { format } from "date-fns";

interface KeywordData {
  id: string;
  keyword: string;
  search_engine: string;
  landing_page: string;
  frequency: number;
  first_seen: string;
  last_seen: string;
  leads_generated: number;
}

interface VisitorSession {
  id: string;
  session_id: string;
  landing_page: string;
  referrer: string;
  search_engine: string;
  search_keywords: string;
  device_type: string;
  browser: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  created_at: string;
}

interface AnalyticsStats {
  totalVisitors: number;
  totalKeywords: number;
  organicTraffic: number;
  topSearchEngine: string;
}

const AdminAnalytics = () => {
  const { user, loading } = useAuth();
  const [keywords, setKeywords] = useState<KeywordData[]>([]);
  const [visitors, setVisitors] = useState<VisitorSession[]>([]);
  const [stats, setStats] = useState<AnalyticsStats>({
    totalVisitors: 0,
    totalKeywords: 0,
    organicTraffic: 0,
    topSearchEngine: ''
  });
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalyticsData();
    }
  }, [user]);

  const loadAnalyticsData = async () => {
    try {
      setLoadingData(true);

      // Fetch search keywords
      const { data: keywordsData, error: keywordsError } = await supabase
        .from('search_keywords')
        .select('*')
        .order('frequency', { ascending: false })
        .limit(50);

      if (keywordsError) throw keywordsError;

      // Fetch recent visitor sessions
      const { data: visitorsData, error: visitorsError } = await supabase
        .from('visitor_sessions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (visitorsError) throw visitorsError;

      setKeywords(keywordsData || []);
      setVisitors(visitorsData || []);

      // Calculate stats
      const totalVisitors = visitorsData?.length || 0;
      const organicVisitors = visitorsData?.filter(v => v.search_engine) || [];
      const organicTraffic = organicVisitors.length;
      
      // Count search engines
      const searchEngines = organicVisitors.reduce((acc: Record<string, number>, visitor) => {
        if (visitor.search_engine) {
          acc[visitor.search_engine] = (acc[visitor.search_engine] || 0) + 1;
        }
        return acc;
      }, {});

      const topSearchEngine = Object.keys(searchEngines).reduce((a, b) => 
        searchEngines[a] > searchEngines[b] ? a : b, ''
      );

      setStats({
        totalVisitors,
        totalKeywords: keywordsData?.length || 0,
        organicTraffic,
        topSearchEngine
      });

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <AdminLogin />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <SEOHead 
        title="Analytics Dashboard - Admin"
        description="View website analytics, search keywords, and visitor data"
      />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="text-muted-foreground">Track visitor behavior and search keywords</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Visitors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalVisitors}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Search Keywords</CardTitle>
            <Search className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalKeywords}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organic Traffic</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.organicTraffic}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Search Engine</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.topSearchEngine || 'N/A'}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="keywords" className="space-y-6">
        <TabsList>
          <TabsTrigger value="keywords">Search Keywords</TabsTrigger>
          <TabsTrigger value="visitors">Recent Visitors</TabsTrigger>
        </TabsList>

        <TabsContent value="keywords">
          <Card>
            <CardHeader>
              <CardTitle>Top Search Keywords</CardTitle>
              <CardDescription>
                Keywords that bring visitors to your website from search engines
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingData ? (
                <div className="text-center py-8">Loading keywords...</div>
              ) : keywords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No search keywords tracked yet. Keywords will appear as visitors find your site through search engines.
                </div>
              ) : (
                <div className="space-y-4">
                  {keywords.map((keyword) => (
                    <div key={keyword.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{keyword.keyword}</span>
                          <Badge variant="secondary">{keyword.search_engine}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Landing page: {keyword.landing_page}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          First seen: {format(new Date(keyword.first_seen), 'MMM d, yyyy')} • 
                          Last seen: {format(new Date(keyword.last_seen), 'MMM d, yyyy')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold">{keyword.frequency}</div>
                        <div className="text-xs text-muted-foreground">visits</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visitors">
          <Card>
            <CardHeader>
              <CardTitle>Recent Visitors</CardTitle>
              <CardDescription>
                Latest visitor sessions and their sources
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingData ? (
                <div className="text-center py-8">Loading visitors...</div>
              ) : visitors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No visitors tracked yet. Visitor data will appear as people visit your website.
                </div>
              ) : (
                <div className="space-y-4">
                  {visitors.map((visitor) => (
                    <div key={visitor.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getDeviceIcon(visitor.device_type)}
                          <span className="font-medium">{visitor.device_type}</span>
                          <Badge variant="outline">{visitor.browser}</Badge>
                          {visitor.search_engine && (
                            <Badge variant="secondary">{visitor.search_engine}</Badge>
                          )}
                        </div>
                        
                        {visitor.search_keywords && (
                          <div className="text-sm mb-1">
                            <span className="text-muted-foreground">Keywords: </span>
                            <span className="font-medium">{visitor.search_keywords}</span>
                          </div>
                        )}
                        
                        <div className="text-sm text-muted-foreground">
                          Landing: {visitor.landing_page}
                        </div>
                        
                        {visitor.referrer && (
                          <div className="text-xs text-muted-foreground">
                            From: {visitor.referrer}
                          </div>
                        )}

                        {(visitor.utm_source || visitor.utm_medium || visitor.utm_campaign) && (
                          <div className="flex gap-2 mt-2">
                            {visitor.utm_source && <Badge variant="outline">Source: {visitor.utm_source}</Badge>}
                            {visitor.utm_medium && <Badge variant="outline">Medium: {visitor.utm_medium}</Badge>}
                            {visitor.utm_campaign && <Badge variant="outline">Campaign: {visitor.utm_campaign}</Badge>}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right text-xs text-muted-foreground">
                        {format(new Date(visitor.created_at), 'MMM d, yyyy HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;